import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { createControllingPersonFormGroup } from '@helpers/controlling-person-form.helper';
import { CompaniesService } from '@services/companies.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyControllingPersonModel, CompanyModel } from '@data-layer/models/companies';
import { IHeaderItem } from '@app/modules/app-layout/interfaces';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { UserBetaFlagModel, UserModel } from '@app/data-layer/models/user';
import { OrganizationsService } from '@services/organizations.service';
import { Observable, ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { SharedSelectors } from '@app/shared/shared-state';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';

@Component({
  selector: 'company-create-page',
  templateUrl: './company-create-page.component.html',
  styleUrls: ['./company-create-page.component.scss'],
})
export class CompanyCreatePageComponent implements OnInit, OnDestroy {
  @Select(SharedSelectors.getAppBetaFlags) public appBetaFlags$: Observable<UserBetaFlagModel[]>;

  public organization: OrganizationModel;
  public company: CompanyModel;
  public formGroup: UntypedFormGroup;
  public firstCompanyId: string;
  public currentUser: UserModel;
  public isHasOnboardingRedesign: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private appLayoutService: AppLayoutService,
    private fb: UntypedFormBuilder,
    private fh: FormHelper,
    private route: ActivatedRoute,
    private organizationsService: OrganizationsService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.updateDashboardHeader();
    this.firstCompanyId = this.route.snapshot.queryParams['isFirstCompany'];

    this.appBetaFlags$.pipe(takeUntil(this.destroy$)).subscribe((flags: UserBetaFlagModel[]) => {
      this.isHasOnboardingRedesign = flags.map((flag: UserBetaFlagModel) => flag.tag).includes(enumUserBetaFlag.onboardingRedesign);
      this.createForm();
    });

    this.companiesService.placement = enumCompanyPlacement.COMPANY_PAGE;

    const firstCompany = this.companiesService.getCompanies()[0];
    this.currentUser = this.companiesService.getUser();
    this.organizationsService
      .getOrganizationByIdFromServer(this.currentUser.defaultOrganizationId || firstCompany?.organizationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((organization) => {
        this.organizationsService.setDefaultOrganization(organization);
        this.organization = organization;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public redirectToView(companyId: number): void {
    this.router.navigate(['/companies', 'account-settings', 'company-view', companyId]);
  }

  private createForm(): void {
    if (!!this.firstCompanyId) {
      this.companiesService.getCompanyByIdFromServer(Number(this.firstCompanyId)).subscribe((company: CompanyModel) => {
        this.company = company;
        this.formGroup = this.fb.group(this.fh.createCompanyFormGroup(this.isHasOnboardingRedesign || company.isUnitedStates));
        this.formGroup.get('id').patchValue(company.id);
        this.formGroup.get('isUnitedStates').patchValue(this.isHasOnboardingRedesign || company.isUnitedStates);
        this.formGroup.get('shippingRole').patchValue(company.shippingRole);
        // #13269 — the save PUT is a full replace: already-stored people must be present in the
        // form, otherwise saving this prefilled create form would wipe them
        this.formGroup.get('partnershipType').patchValue(company.partnershipType);
        const personsArray: UntypedFormArray = this.formGroup.get('controllingPersons') as UntypedFormArray;
        (company.controllingPersons || []).forEach((person: CompanyControllingPersonModel) =>
          personsArray.push(createControllingPersonFormGroup(person)),
        );
        this.cdr.markForCheck();
      });
      // #7943
    } else {
      this.formGroup = this.fb.group(
        this.fh.createCompanyFormGroup(this.isHasOnboardingRedesign || this.companiesService.getCompanies()[0]?.isUnitedStates),
      );
      this.formGroup
        .get('isUnitedStates')
        .patchValue(this.isHasOnboardingRedesign || this.companiesService.getCompanies()[0]?.isUnitedStates);
      this.formGroup.get('shippingRole').patchValue(this.companiesService.getCompanies()[0]?.shippingRole);
    }
  }

  private updateDashboardHeader(): void {
    const title: IHeaderItem[] = [];
    title.push({
      key: 'MENU.ACCOUNT_SETTINGS',
      link: null,
    });
    title.push({
      key: `LABELS.CREATE_IMPORTER_OF_RECORD`,
      link: null,
    });
    this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });
  }
}
